import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Skeleton, Tooltip } from "antd";
import { ViewInterview } from "../../candidates/misc/useCandidate";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { reprocess_video } from "../../../services/api/interview";
import {
  ArrowLeft,
  CheckCircle,
  Info,
  Mic,
  Video,
  XCircle,
  NotebookPen,
  CheckCheck,
} from "lucide-react";
import AVSyncPlayer from "../../../components/AVSyncPlayer";
import { PieChart } from "react-minimal-pie-chart";
import {
  MOCK_CANDIDATE_EVALUATION,
  MOCK_CONVERSATION_HISTORY,
} from "./mockData";
import img from "../../../constant";

const InterviewReports: React.FC = () => {
  const [searchParams] = useSearchParams();
  const interviewId = searchParams.get("interviewId") as string;
  const id = searchParams.get("candidateId") as string;
  const [activeTab, setActiveTab] = useState<
    "recommendation" | "strength" | "flags" | "notes"
  >("recommendation");
  const [activeResumeTab, setActiveResumeTab] = useState<
    "recommendation" | "strength" | "flags"
  >("recommendation");
  const [mediaTab, setMediaTab] = useState<"video" | "audio">("video");
  const [summaryTab, setSummaryTab] = useState<
    "summary" | "rationale" | "notes" | "confidence"
  >("summary");
  const [isReprocessing, setIsReprocessing] = useState(false);
  const [videoSourceTab, setVideoSourceTab] = useState<"backend" | "frontend">("backend");

  const handleReprocessVideo = async () => {
    if (!interviewId) return;
    try {
      setIsReprocessing(true);
      const res = await reprocess_video(interviewId);
      toast.success(res?.message || "Video reprocessing started successfully.");
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to reprocess video.");
    } finally {
      setIsReprocessing(false);
    }
  };

  const {
    candidateEvaluation: apiCandidateEvaluation,
    loading,
    conversationHistory: apiConversationHistory,
    error,
    candidateVideoUrl,
    candidateFrontendVideoUrl,
    candidateAudioUrl,
    candidateFeedback,
    candidateScreenshots,
    candidateEmail,
  } = ViewInterview(interviewId, id);

  // Use mock data if specific error occurs, otherwise use API data
  const shouldUseMockData =
    error === "No conversation found for this interview" ||
    error === "No conversatin found for this interview"; // Handling typo from user request just in case

  const candidateEvaluation = shouldUseMockData
    ? MOCK_CANDIDATE_EVALUATION
    : apiCandidateEvaluation;
  const conversationHistory = shouldUseMockData
    ? MOCK_CONVERSATION_HISTORY
    : apiConversationHistory;
  const feedback = shouldUseMockData ? null : candidateFeedback;

  // Show error only if it's NOT the specific error we're handling with mock data
  const showRealError = error && !shouldUseMockData;

  // Get candidate first name
  const getFirstName = () => {
    const fullName = candidateEvaluation?.candidateName || "User";
    return fullName.split(" ")[0];
  };

  const getInitials = (): string => {
    const name = candidateEvaluation?.candidateName;
    if (!name) return "U";
    const [firstName, lastName] = name.trim().split(" ");
    const firstInitial = firstName?.charAt(0)?.toUpperCase() ?? "";
    const lastInitial = lastName?.charAt(0)?.toUpperCase() ?? "";
    return firstInitial + lastInitial;
  };

  // Format timestamp to readable date and time
  const formatTimestamp = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleString();
    } catch {
      return timestamp;
    }
  };

  const formatTimeOnly = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true, // Use false for 24-hour format
      });
    } catch {
      return timestamp;
    }
  };

  // Calculate overall score from competency areas
  const calculateOverallScore = () => {
    const areas = candidateEvaluation?.competencyAreas;
    if (!areas || !Array.isArray(areas)) return 0;

    const totalScore = (areas as any[]).reduce(
      (sum: number, area: any) => sum + (Number(area.score) || 0),
      0,
    );
    return Math.round(totalScore / areas.length);
  };

  const overallScore = calculateOverallScore();

  const getRecommendationStatusColor = (status: string) => {
    const s = status?.toLowerCase() || "";
    if (s.includes("strong hire")) return "bg-purple-600 hover:bg-purple-700";
    if (s.includes("do not hire")) return "bg-red-600 hover:bg-red-700";
    if (s.includes("hire"))
      return "bg-green-600 hover:bg-green-700 outline-none border-none";
    if (s.includes("consider"))
      return "bg-orange-500 hover:bg-orange-600 outline-none border-none";
    return "bg-primary hover:bg-green-600 outline-none border-none";
  };

  // Map competency areas to core rubrics with colors
  const getCoreRubrics = () => {
    // if (!candidateEvaluation?.competencyAreas) return [];
    if (!candidateEvaluation?.evaluationData?.interviewDetails) return [];

    const colorMap: any = {
      0: {
        bg: "bg-red-100",
        text: "text-red-600",
        badge: "bg-red-500",
        bg2: "#ef4444",
        bg3: "#f87171",
      },
      1: {
        bg: "bg-orange-100",
        text: "text-orange-600",
        badge: "bg-orange-500",
        bg2: "#f97316",
        bg3: "#fb923c",
      },
      2: {
        bg: "bg-green-100",
        text: "text-green-600",
        badge: "bg-green-500",
        bg2: "#22c55e",
        bg3: "#4ade80",
      },
      3: {
        bg: "bg-purple-100",
        text: "text-purple-600",
        badge: "bg-purple-500",
        bg2: "#8b5cf6",
        bg3: "#c084fc",
      },
      4: {
        bg: "bg-blue-100",
        text: "text-blue-600",
        badge: "bg-blue-500",
        bg2: "#3b82f6",
        bg3: "#60a5fa",
      },
      5: {
        bg: "bg-brown-100",
        text: "text-brown-600",
        badge: "bg-brown-500",
        bg2: "#f2e8e5",
        bg3: "#a18072",
      },
      6: {
        bg: "bg-amber-100",
        text: "text-amber-600",
        badge: "bg-amber-500",
        bg2: "#fef3c7",
        bg3: "#fbbf24",
      },
    };

    // return candidateEvaluation.competencyAreas
    return candidateEvaluation.evaluationData.interviewDetails
      .slice(0, 5)
      .map((area, index) => ({
        ...area,
        ...colorMap[index],
        badge: area.weight,
        // badge: Math.ceil((area.score / 100) * 3), // Convert to 1-3 scale
      }));
  };

  if (showRealError) {
    return (
      <div className="p-4">
        <div className="text-red-500">{error}</div>
        <button
          onClick={() => history.back()}
          className="text-blue-600 hover:text-blue-800 mt-4 inline-block"
        >
          Back to Interview
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="w-full py-5">
        <div className="max-w-[1200px] mx-auto px-4">
          <Skeleton active paragraph={{ rows: 20 }} />
        </div>
      </div>
    );
  }

  if (!candidateEvaluation) {
    return (
      <div className="w-full py-5">
        <div className="max-w-[1200px] mx-auto px-4 text-center text-gray-500">
          Report not found
        </div>
      </div>
    );
  }

  const coreRubrics = getCoreRubrics();

  return (
    <div className="w-full py-4 bg-gray-50">
      <div className="max-w-[1200px] mx-auto px-4">
        <div className="flex items-center gap-4 mb-2">
          <div
            onClick={() => history.back()}
            className="text-blue-600 hover:text-blue-800 cursor-pointer"
          >
            <ArrowLeft className="w-6 h-6" />
          </div>
          <p className="text-[25px]">
            Assessment Report |{" "}
            <strong>{candidateEvaluation.position} | </strong>
            <span className="text-[15px]">
              {formatTimestamp(candidateEvaluation.createdAt)}
            </span>
          </p>
        </div>
        {/* Header Section - Profile and Summary */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-3 mb-3">
          {/* Profile Card */}
          <div className="col-span-1 bg-white rounded-lg p-4 shadow-sm border border-gray-200 text-center">
            <div className="gap-2 mx-auto">
              <div className="w-[100px] h-[100px] rounded-full overflow-hidden flex items-center justify-center text-white text-[30px] mx-auto border-2 border-gray-100">
                {candidateEvaluation.candidateImage ? (
                  <img
                    src={candidateEvaluation.candidateImage}
                    alt={candidateEvaluation.candidateName}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <img
                    src={candidateScreenshots || img.avatar}
                    alt="Avatar"
                    className="w-full h-full object-cover"
                  />
                )}
                {/* {getInitials()} */}
                {/* {candidateEvaluation.candidateName?.charAt(0) || "NA"} */}
              </div>
              <div className="flex items-center justify-center">
                <div className=" gap-2 pt-1">
                  <h2 className="text-lg font-semibold text-gray-900">
                    {candidateEvaluation.candidateName || "John Doe"}
                  </h2>
                  <h4 className="text-[12px] font-normal text-gray-700">
                    {candidateEmail || ""}
                  </h4>
                </div>
              </div>
            </div>
          </div>

          {/* Circular Score */}
          <div className="col-span-1 bg-white rounded-lg p-4 shadow-sm border border-gray-200 text-center">
            <div className="flex-shrink-0 mx-auto">
              {/* Outer Circle with Status Color */}
              <div className="relative w-32 h-32 mx-auto">
                {/* Outer Ring */}
                <svg
                  className="absolute inset-0 w-full h-full -rotate-90"
                  viewBox="0 0 100 100"
                >
                  <circle
                    cx="50"
                    cy="50"
                    r="45"
                    fill="none"
                    stroke="#e5e7eb"
                    strokeWidth="4"
                  />
                  <circle
                    cx="50"
                    cy="50"
                    r="45"
                    fill="none"
                    stroke={(() => {
                      const status = (
                        candidateEvaluation?.recommendationStatus || ""
                      ).toLowerCase();
                      if (status.includes("strong hire")) return "#9333ea";
                      if (status.includes("do not hire")) return "#dc2626";
                      if (status.includes("hire")) return "#16a34a";
                      if (status.includes("consider")) return "#ea580c";
                      return "#6b7280";
                    })()}
                    strokeWidth="4"
                    strokeDasharray={`${(Math.round(
                      candidateEvaluation?.evaluationData?.summary
                        ?.overallScore ||
                      overallScore ||
                      0,
                    ) /
                      100) *
                      283
                      } 283`}
                    className="transition-all duration-500"
                  />
                </svg>

                {/* Score Text on Outer Ring */}
                <div className="absolute top-2 left-1/2 -translate-x-1/2 -translate-y-1">
                  <span
                    className={`text-[10px] font-semibold ${(() => {
                      const status = (
                        candidateEvaluation?.recommendationStatus || ""
                      ).toLowerCase();
                      if (status.includes("strong hire"))
                        return "text-purple-600";
                      if (status.includes("do not hire")) return "text-red-600";
                      if (status.includes("hire")) return "text-green-600";
                      if (status.includes("consider")) return "text-orange-600";
                      return "text-gray-600";
                    })()}`}
                  >
                    {Math.round(
                      candidateEvaluation?.evaluationData?.summary
                        ?.overallScore ||
                      overallScore ||
                      0,
                    )}
                    /100%
                  </span>
                </div>

                {/* Inner Pie Chart */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="relative w-20 h-20">
                    <PieChart
                      data={coreRubrics.map((item) => ({
                        title: item.area,
                        value: item.score,
                        color: item.bg2,
                      }))}
                      lineWidth={20}
                      rounded
                      animate
                      viewBoxSize={[100, 100]}
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-[32px] font-bold text-gray-900">
                        {Math.round(
                          candidateEvaluation?.evaluationData?.summary
                            ?.overallScore ||
                          overallScore ||
                          0,
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <button
              className={`mt-2 text-center text-white text-sm md:text-[16px] font-medium py-1 px-4 rounded-full transition-colors ${getRecommendationStatusColor(
                candidateEvaluation?.recommendationStatus || "",
              )}`}
            >
              {candidateEvaluation?.recommendationStatus || "N/A"}
            </button>
          </div>

          {/* Summary Card */}
          <div className="lg:col-span-3 bg-white rounded-lg px-4 py-3 shadow-sm border border-gray-200">
            <div className=" items-start gap-6">
              {/* Tabs */}
              <div className="flex gap-2 mb-4 border-b border-gray-200">
                <button
                  onClick={() => setSummaryTab("summary")}
                  className={`px-4 py-2 text-sm font-medium transition-colors rounded-full ${summaryTab === "summary"
                    ? "text-white bg-black rounded-full"
                    : "text-gray-900 hover:text-gray-800"
                    }`}
                >
                  <div className="flex items-center gap-1">
                    <CheckCircle className="w-4 h-4" />
                    Summary
                  </div>
                </button>
                <button
                  onClick={() => setSummaryTab("rationale")}
                  className={`px-4 py-2 text-sm font-medium transition-colors rounded-full ${summaryTab === "rationale"
                    ? "text-white bg-black rounded-full"
                    : "text-gray-800 hover:text-gray-700"
                    }`}
                >
                  <div className="flex items-center gap-1">
                    <CheckCircle className="w-4 h-4" />
                    Rationale
                  </div>
                </button>
                <button
                  onClick={() => setSummaryTab("notes")}
                  className={`px-4 py-2 text-sm font-medium transition-colors rounded-full ${summaryTab === "notes"
                    ? "text-white bg-black rounded-full"
                    : "text-gray-600 hover:text-gray-800"
                    }`}
                >
                  <div className="flex items-center gap-1">
                    <NotebookPen className="w-4 h-4" />
                    Notes
                  </div>
                </button>
                <button
                  onClick={() => setSummaryTab("confidence")}
                  className={`px-4 py-2 text-sm font-medium transition-colors rounded-full ${summaryTab === "confidence"
                    ? "text-white bg-black rounded-full"
                    : "text-gray-600 hover:text-gray-800"
                    }`}
                >
                  <div className="flex items-center gap-1">
                    <CheckCircle className="w-4 h-4" />
                    Confidence
                  </div>
                </button>
              </div>

              {/* Tab Content */}
              <div className="min-h-[120px]">
                {summaryTab === "summary" && (
                  <div>
                    {/* Summary Text */}
                    <div className="flex-1">
                      <h3 className="text-sm font-semibold text-gray-900 mb-2">
                        Summary
                      </h3>
                      <p className="text-xs text-gray-600 leading-relaxed">
                        {candidateEvaluation.recommendationSummary || "N/A"}
                      </p>
                    </div>
                  </div>
                )}
                {summaryTab === "rationale" && (
                  <div>
                    {/* Summary Text */}
                    <div className="flex-1">
                      <h3 className="text-sm font-semibold text-gray-900 mb-2">
                        Confidence Rationale
                      </h3>
                      <p className="text-xs text-gray-600 leading-relaxed">
                        {candidateEvaluation.evaluationData?.summary
                          ?.confidenceRationale || "N/A"}
                      </p>
                    </div>
                  </div>
                )}
                {summaryTab === "notes" && (
                  <div className="mt-1">
                    <p className="text-[14px] font-semibold text-gray-900 mb-1">
                      Hiring Manager Notes
                    </p>
                    <div className="min-h-[40px]">
                      <p className="text-[15px] text-gray-700 leading-relaxed">
                        {candidateEvaluation?.evaluationData?.summary
                          ?.hiringManagerNotes || "No notes available."}
                      </p>
                    </div>
                    <p className="text-[14px] font-semibold text-gray-900 mb-1 mt-2">
                      Interviewer's Feedback
                    </p>
                    <div className="">
                      <p className="text-[15px] text-gray-700 leading-relaxed">
                        {candidateEvaluation?.evaluationData?.additionalNotes
                          ?.interviewerFeedback || "No notes available."}
                      </p>
                    </div>
                  </div>
                )}
                {summaryTab === "confidence" && (
                  <div className="mt-1">
                    <p className="text-[14px] font-semibold text-gray-900 mb-2">
                      Confidence Level
                    </p>
                    <p className="text-[15px] text-gray-700 leading-relaxed capitalize">
                      {candidateEvaluation?.evaluationData?.summary
                        ?.confidenceLevel || "No notes available."}
                    </p>
                  </div>
                )}
              </div>

              {/* end */}
            </div>
          </div>
        </div>

        {/* Core Rubrics Section */}
        <div className="mb-5">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">
            Core Competencies
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {/* {candidateEvaluation?.interviewDetails.map((rubric, index) => ( */}
            {coreRubrics.map((rubric, index) => (
              <div
                key={index}
                className={`${rubric.bg} rounded-lg p-4 relative group cursor-help`}
              >
                <div
                  className={`absolute top-1 left-2 text-gray-500 w-8 h-8 rounded-full flex items-center justify-center text-[15px]`}
                >
                  <Info />
                </div>
                <div
                  className={`absolute top-2 right-2 text-white w-8 h-8 rounded-full flex items-center justify-center text-[25px]`}
                  style={{ backgroundColor: rubric.bg3 }}
                >
                  {rubric.badge}
                </div>
                <div className="pt-2">
                  <div
                    className={` font-medium text-black flex items-start mb-1`}
                  >
                    {/* ${rubric.text} */}
                    <span className="text-[25px] md:text-[60px]">
                      {rubric.score}
                    </span>
                    <span className="text-lg md:text-[19px] pt-4">%</span>
                  </div>
                  <div className="text-[17.5px] font-medium text-gray-700 capitalize">
                    {rubric.rubric}
                  </div>
                </div>

                {/* Hover Tooltip */}
                {rubric.reason && (
                  <div className="invisible group-hover:visible absolute left-1/2 -translate-x-1/2 bottom-full mb-2 z-50 bg-gray-900 text-white text-xs rounded-lg p-3 w-64 shadow-xl">
                    <p className="leading-relaxed">{rubric.reason}</p>
                    <div className="absolute left-1/2 -translate-x-1/2 top-full border-4 border-transparent border-t-gray-900"></div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Core Assessment and More Rubrics Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-3">
          {/* Core Assessment */}
          <div className="bg-white rounded-lg px-4 py-3 shadow-sm border border-gray-200">
            <h3 className="text-base font-semibold text-gray-900 mb-4">
              Core Assessment
            </h3>

            {/* Tabs */}
            <div className="flex gap-2 mb-4 border-b border-gray-200">
              <button
                onClick={() => setActiveTab("recommendation")}
                className={`px-4 py-2 text-sm font-medium transition-colors rounded-full ${activeTab === "recommendation"
                  ? "text-white bg-black rounded-full"
                  : "text-blue-900 hover:text-blue-800"
                  }`}
              >
                <div className="flex items-center gap-1">
                  <CheckCircle className="w-4 h-4" />
                  Key Gaps
                </div>
              </button>
              <button
                onClick={() => setActiveTab("strength")}
                className={`px-4 py-2 text-sm font-medium transition-colors rounded-full ${activeTab === "strength"
                  ? "text-white bg-black rounded-full"
                  : "text-green-800 hover:text-green-700"
                  }`}
              >
                <div className="flex items-center gap-1">
                  <CheckCircle className="w-4 h-4" />
                  Strength
                </div>
              </button>
              <button
                onClick={() => setActiveTab("flags")}
                className={`px-4 py-2 text-sm font-medium transition-colors rounded-full ${activeTab === "flags"
                  ? "text-white bg-black rounded-full"
                  : "text-red-500 hover:text-red-700"
                  }`}
              >
                <div className="flex items-center gap-1">
                  <XCircle className="w-4 h-4" />
                  Flags
                </div>
              </button>
              <button
                onClick={() => setActiveTab("notes")}
                className={`px-4 py-2 text-sm font-medium transition-colors rounded-full ${activeTab === "notes"
                  ? "text-white bg-black rounded-full"
                  : "text-gray-600 hover:text-gray-800"
                  }`}
              >
                <div className="flex items-center gap-1">
                  <NotebookPen className="w-4 h-4" />
                  Notes
                </div>
              </button>
            </div>

            {/* Tab Content */}
            <div className="min-h-[120px]">
              {activeTab === "recommendation" && (
                <div>
                  <div className="flex items-center justify-between mb-2 font-abel">
                    <p className="text-sm text-gray-700">
                      Recommendation:{" "}
                      <span className="font-semibold text-primary">
                        {candidateEvaluation?.evaluationData?.summary
                          ?.recommendation ||
                          candidateEvaluation?.recommendationStatus ||
                          "N/A"}
                      </span>
                    </p>
                    <p className="text-sm text-gray-700">
                      Overall Score:{" "}
                      <span className="font-semibold text-blue-600">
                        {candidateEvaluation?.evaluationData?.summary
                          ?.overallScore ||
                          overallScore ||
                          0}
                        %
                      </span>
                    </p>
                  </div>
                  <div>
                    <p className="text-[14px] font-semibold text-gray-700 mb-1">
                      Key Gaps
                    </p>
                    <ul className="space-y-1 text-sm text-gray-700">
                      {(
                        candidateEvaluation?.evaluationData?.summary?.keyGaps ||
                        candidateEvaluation?.areasOfConcern
                      )?.map((gap: string, index: number) => (
                        <li key={index} className="flex items-start gap-2">
                          <span className="text-red-400">•</span>
                          <span>{gap || "N/A"}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
              {activeTab === "strength" && (
                <div>
                  <p className="text-[14px] font-semibold text-700 mb-1">
                    Key Strengths
                  </p>
                  <ul className="space-y-2 text-sm text-gray-700">
                    {(
                      candidateEvaluation?.evaluationData?.summary
                        ?.keyStrengths || candidateEvaluation?.strengths
                    )?.map((strength: string, index: number) => (
                      <li key={index} className="flex items-start gap-2">
                        <span className="text-green-500 font-medium">•</span>
                        <span>{strength || "N/A"}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {activeTab === "flags" && (
                <div className="space-y-4">
                  {candidateEvaluation?.evaluationData?.summary
                    ?.riskFactors && (
                      <div>
                        <p className="text-[14px] font-semibold text-700 mb-1">
                          Risk Factors
                        </p>
                        <ul className="space-y-1 text-sm text-gray-700">
                          {candidateEvaluation?.evaluationData?.summary?.riskFactors?.map(
                            (risk: string, index: number) => (
                              <li key={index} className="flex items-start gap-2">
                                <span className="text-orange-400">•</span>
                                <span>{risk || "N/A"}</span>
                              </li>
                            ),
                          )}
                        </ul>
                      </div>
                    )}
                </div>
              )}
              {activeTab === "notes" && (
                <div>
                  <div className="mt-1">
                    <p className="text-[14px] font-semibold text-gray-900 mb-1">
                      Comparison to typical Candidate
                    </p>
                    <p className="text-[15px] text-gray-700 leading-relaxed">
                      {candidateEvaluation?.evaluationData?.additionalNotes
                        ?.comparisonToTypicalCandidate || "No notes available."}
                    </p>
                  </div>
                  <div className="mt-2">
                    <p className="text-[14px] font-semibold text-gray-900 mb-2">
                      Development Areas
                    </p>
                    <ul className="space-y-1 text-sm text-gray-700">
                      {candidateEvaluation?.evaluationData?.summary?.developmentAreas?.map(
                        (developmentArea: string, index: number) => (
                          <li key={index} className="flex items-start gap-2">
                            <span className="text-brown-400">•</span>
                            <span>{developmentArea || "N/A"}</span>
                          </li>
                        ),
                      )}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* More Rubrics */}
          <div className="bg-white rounded-lg px-4 py-3 shadow-sm border border-gray-200">
            <h3 className="text-base font-semibold text-gray-900 mb-4">
              More Competencies
            </h3>

            <div className="space-y-4">
              {(
                candidateEvaluation?.evaluationData?.interviewDetails ||
                // candidateEvaluation.evaluationData?.interviewDetails?.slice(0, 3)
                candidateEvaluation.competencyAreas?.slice(0, 3)
              ).map((item: any, index: number) => {
                const colors = [
                  "bg-red-400",
                  "bg-orange-500",
                  "bg-green-500",
                  "bg-purple-500",
                  "bg-blue-500",
                  "bg-brown-500",
                  "bg-amber-500",
                ];
                const label = item.rubric || item.area;
                const score = item.rawScore || item.score;
                const comments = item.reason || "No comments";
                const weight = item.weight || item.weight;
                return (
                  <div key={index}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <Tooltip
                          title={comments}
                          placement="top"
                          className="flex items-center gap-1"
                        >
                          <span className="text-sm text-gray-700 cursor-help">
                            {label}
                          </span>
                          <div
                            className={`text-gray-500 w-4 h-4 rounded-full flex items-center justify-center text-[12px]`}
                          >
                            <Info />
                          </div>
                        </Tooltip>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-gray-700">
                          ({weight})
                        </span>
                        <span className="text-sm font-semibold text-blue-600">
                          {score}%
                        </span>
                      </div>
                    </div>
                    <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${colors[index % colors.length]
                          } rounded-full transition-all duration-300`}
                        style={{ width: `${score}%` }}
                      ></div>
                    </div>
                    {/* Hover Tooltip - Removed in favor of Tooltip component */}
                    {/* {comments && (
                      <div className="invisible group-hover:visible absolute left-1/2 -translate-x-1/2 bottom-full mb-2 z-50 bg-gray-900 text-white text-xs rounded-lg p-3 w-64 shadow-xl">
                        <p className="leading-relaxed">{comments}</p>
                        <div className="absolute left-1/2 -translate-x-1/2 top-full border-4 border-transparent border-t-gray-900"></div>
                      </div>
                    )} */}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Video and Transcript Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-3">
          {/* Video Section */}
          <div className="bg-white rounded-lg px-4 py-3 shadow-sm border border-gray-200 overflow-hidden">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-gray-900 mb-1">
                Media
              </h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleReprocessVideo}
                  disabled={isReprocessing}
                  className="px-3 py-1 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
                >
                  {isReprocessing ? "Reprocessing..." : "Rerender Video"}
                </button>
                <div className="flex bg-gray-100 rounded-lg p-1">
                  <button
                    onClick={() => setMediaTab("video")}
                    className={`flex items-center gap-1 px-3 py-1 rounded-md text-sm font-medium transition-all ${mediaTab === "video"
                      ? "bg-white text-black shadow-sm"
                      : "text-gray-500 hover:text-gray-700"
                      }`}
                  >
                    <Video className="w-4 h-4" />
                    Video
                  </button>
                  <button
                    onClick={() => setMediaTab("audio")}
                    className={`flex items-center gap-1 px-3 py-1 rounded-md text-sm font-medium transition-all ${mediaTab === "audio"
                      ? "bg-white text-black shadow-sm"
                      : "text-gray-500 hover:text-gray-700"
                      }`}
                  >
                    <Mic className="w-4 h-4" />
                    Audio
                  </button>
                </div>
              </div>
            </div>

            {/* Video Source Sub-tabs (only shown when mediaTab === "video") */}
            {mediaTab === "video" && (
              <div className="flex bg-gray-100 rounded-lg p-1 w-fit">
                <button
                  onClick={() => setVideoSourceTab("backend")}
                  className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${videoSourceTab === "backend"
                    ? "bg-white text-black shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                    }`}
                >
                  Backend
                </button>
                <button
                  onClick={() => setVideoSourceTab("frontend")}
                  className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${videoSourceTab === "frontend"
                    ? "bg-white text-black shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                    }`}
                >
                  Frontend
                </button>
              </div>
            )}

            <div className="relative w-full h-full min-h-[400px] flex mt-1  rounded-lg overflow-hidden">
              {mediaTab === "video" ? (
                <AVSyncPlayer
                  videoSrc={videoSourceTab === "frontend" ? candidateFrontendVideoUrl : candidateVideoUrl}
                  audioSrc={candidateAudioUrl}
                  width="100%"
                  height="100%"
                  audioDelay={-2}
                />
              ) : (
                <div
                  className={`w-full h-full flex flex-col items-center justify-center p-6 text-gray-500 object-cover bg-cover bg-center bg-no-repeat ${candidateScreenshots ? "" : "bg-white"}`}
                  style={{ backgroundImage: `url(${candidateScreenshots})` }}
                >
                  <div className="w-16 h-16 rounded-full bg-gray-800 flex items-center justify-center mb-4">
                    <Mic className="w-8 h-8 text-blue-400" />
                  </div>
                  <h4 className="text-lg font-medium mb-6">Audio Recording</h4>
                  <audio
                    controls
                    className="w-full max-w-md"
                    src={candidateAudioUrl}
                  >
                    Your browser does not support the audio element.
                  </audio>
                </div>
              )}
            </div>
          </div>

          {/* Interview Transcript */}
          <div className="bg-white rounded-lg px-4 py-3 shadow-sm border border-gray-200 overflow-hidden">
            <h3 className="text-base font-semibold text-gray-900 mb-1">
              Interview Transcript
            </h3>
            <div className="h-[395px] overflow-y-auto space-y-3">
              {conversationHistory?.map((conversation, index) => (
                <div key={index}>
                  {/* AI Question */}
                  <div className="mb-2 border-b border-gray-200">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="px-2 py-0.5 bg-gray-200 text-gray-700 rounded text-[12px] font-medium">
                        AI
                      </span>
                      <span className="text-[12px] text-gray-500">
                        {formatTimeOnly(conversation.questionTimestamp)}
                      </span>
                    </div>
                    <p className="text-[14px] text-gray-700 ml-1">
                      {conversation.aiQuestion}
                    </p>
                  </div>

                  {/* User Response */}
                  <div className="mb-3 border-b border-gray-200">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-[12px] font-medium">
                        {getFirstName()}
                      </span>
                      <span className="text-[12px] text-gray-500">
                        {formatTimestamp(conversation.responseTimestamp)}
                      </span>
                    </div>
                    <p className="text-[14px] text-gray-700 ml-1">
                      {conversation.userResponse}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Cognitive & Communication Assessment */}
        {(candidateEvaluation?.evaluationData?.cognitiveAssessment ||
          candidateEvaluation?.evaluationData?.communicationAssessment) && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-3">
              {/* Cognitive Assessment */}
              {candidateEvaluation?.evaluationData?.cognitiveAssessment && (
                <div className="bg-white rounded-lg px-4 py-3 shadow-sm border border-gray-200">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-base font-semibold text-gray-900">
                      Cognitive Assessment
                    </h3>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">
                        Overall Score:
                      </span>
                      <span className="text-lg font-bold text-blue-600">
                        {candidateEvaluation.evaluationData.cognitiveAssessment
                          .overallCognitiveScore || 0}
                        %
                      </span>
                    </div>
                  </div>
                  <div className="space-y-3">
                    {[
                      {
                        key: "clarity",
                        label: "Clarity",
                        data: candidateEvaluation.evaluationData
                          .cognitiveAssessment.clarity,
                      },
                      {
                        key: "criticalThinking",
                        label: "Critical Thinking",
                        data: candidateEvaluation.evaluationData
                          .cognitiveAssessment.criticalThinking,
                      },
                      {
                        key: "problemSolving",
                        label: "Problem Solving",
                        data: candidateEvaluation.evaluationData
                          .cognitiveAssessment.problemSolving,
                      },
                      {
                        key: "decisionMaking",
                        label: "Decision Making",
                        data: candidateEvaluation.evaluationData
                          .cognitiveAssessment.decisionMaking,
                      },
                      {
                        key: "logicalReasoning",
                        label: "Logical Reasoning",
                        data: candidateEvaluation.evaluationData
                          .cognitiveAssessment.logicalReasoning,
                      },
                    ].map((item) => (
                      <div key={item.key}>
                        <div className="flex items-center justify-between mb-1">
                          <Tooltip
                            title={item.data?.reason}
                            placement="top"
                            className="flex items-center gap-1"
                          >
                            <span className="text-sm text-gray-700 cursor-help">
                              {item.label}
                            </span>
                            <div
                              className={`text-gray-500 w-4 h-4 rounded-full flex items-center justify-center text-[12px]`}
                            >
                              <Info />
                            </div>
                          </Tooltip>
                          <span className="text-sm font-semibold text-blue-600">
                            {item.data?.score || 0}%
                          </span>
                        </div>
                        <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-blue-500 transition-all duration-300"
                            style={{ width: `${item.data?.score || 0}%` }}
                          ></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Communication Assessment */}
              {candidateEvaluation?.evaluationData?.communicationAssessment && (
                <div className="bg-white rounded-lg px-4 py-3 shadow-sm border border-gray-200">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-base font-semibold text-gray-900">
                      Communication Assessment
                    </h3>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">
                        Overall Score:
                      </span>
                      <span className="text-lg font-bold text-green-600">
                        {candidateEvaluation.evaluationData
                          .communicationAssessment.overallCommunicationScore || 0}
                        %
                      </span>
                    </div>
                  </div>
                  <div className="space-y-3">
                    {[
                      {
                        key: "fluency",
                        label: "Fluency",
                        data: candidateEvaluation.evaluationData
                          .communicationAssessment.fluency,
                      },
                      {
                        key: "grammar",
                        label: "Grammar",
                        data: candidateEvaluation.evaluationData
                          .communicationAssessment.grammar,
                      },
                      {
                        key: "coherence",
                        label: "Coherence",
                        data: candidateEvaluation.evaluationData
                          .communicationAssessment.coherence,
                      },
                      {
                        key: "comprehension",
                        label: "Comprehension",
                        data: candidateEvaluation.evaluationData
                          .communicationAssessment.comprehension,
                      },
                      {
                        key: "vocabularySufficiency",
                        label: "Vocabulary Sufficiency",
                        data: candidateEvaluation.evaluationData
                          .communicationAssessment.vocabularySufficiency,
                      },
                    ].map((item) => (
                      <div key={item.key}>
                        <div className="flex items-center justify-between mb-1">
                          <Tooltip
                            title={item.data?.reason}
                            placement="top"
                            className="flex items-center gap-1"
                          >
                            <span className="text-sm text-gray-700 cursor-help">
                              {item.label}
                            </span>
                            <div
                              className={`text-gray-500 w-4 h-4 rounded-full flex items-center justify-center text-[12px]`}
                            >
                              <Info />
                            </div>
                          </Tooltip>
                          <span className="text-sm font-semibold text-green-600">
                            {item.data?.score || 0}%
                          </span>
                        </div>
                        <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-green-500 transition-all duration-300"
                            style={{ width: `${item.data?.score || 0}%` }}
                          ></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

        {/* Screening & Assessment Questions */}
        {(candidateEvaluation?.pretestQA?.length > 0 ||
          candidateEvaluation?.assessmentQA?.length > 0) && (
            <div className="bg-white rounded-lg px-4 py-3 shadow-sm border border-gray-200 mb-3">
              <h3 className="text-base font-semibold text-gray-900 mb-3">
                Screening & Assessment
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Pretest Questions */}
                {candidateEvaluation?.pretestQA?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wide border-b border-gray-100 pb-1">
                      Pre-Test Questions
                    </h4>
                    <div className="space-y-3">
                      {candidateEvaluation.pretestQA.map((qa, idx) => (
                        <div key={idx} className="bg-gray-50 rounded p-3 text-sm">
                          <p className="font-medium text-gray-800 mb-1">
                            {qa.question}
                          </p>
                          <p className="text-gray-600 bg-white border border-gray-200 p-2 rounded mt-1">
                            <span className="font-semibold text-xs text-blue-600 mr-2 uppercase">
                              Answer:
                            </span>
                            {qa.answer}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Assessment Questions */}
                {candidateEvaluation?.assessmentQA?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wide border-b border-gray-100 pb-1">
                      Assessment Questions
                    </h4>
                    <div className="space-y-3">
                      {candidateEvaluation.assessmentQA.map((qa, idx) => (
                        <div key={idx} className="bg-gray-50 rounded p-3 text-sm">
                          <p className="font-medium text-gray-800 mb-1">
                            {qa.question}
                          </p>
                          <p className="text-gray-600 bg-white border border-gray-200 p-2 rounded mt-1">
                            <span className="font-semibold text-xs text-blue-600 mr-2 uppercase">
                              Answer:
                            </span>
                            {qa.answer}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

        {/* Feedback Section */}
        {feedback && (
          <div className="bg-white rounded-lg px-4 py-3 shadow-sm border border-gray-200 mb-3">
            <h3 className="text-base font-semibold text-gray-900 mb-3 font-abel">
              Candidate Feedback
            </h3>
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <div className="flex items-center gap-1 bg-blue-50 px-3 py-1 rounded-full">
                  <span className="text-blue-600 font-semibold">
                    {feedback.ratings}
                  </span>
                  <span className="text-yellow-500">★</span>
                </div>
              </div>
              <div className="flex-1 italic text-gray-700 text-sm bg-gray-50 p-3 rounded-lg border border-gray-100">
                "{feedback.review}"
              </div>
            </div>
          </div>
        )}

        {/* Resume Assessment Section */}
        {/* <div className="bg-white rounded-lg px-4 py-3 shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Resume Assessment
          </h3>

          <div className="flex items-start gap-6">
            <div className="flex-shrink-0">
              <div className="relative w-24 h-24">
                <PieChart
                  data={[
                    { title: "Score", value: 50, color: "#60A5FA" },
                    { title: "Remaining", value: 20, color: "#E5E7EB" },
                    { title: "Extra", value: 30, color: "#22c55e" },
                  ]}
                  lineWidth={20}
                  rounded
                  animate
                  viewBoxSize={[100, 100]}
                  startAngle={270}
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-2xl text-gray-900">90</span>
                </div>
              </div>
              <span className="mt-2 inline-block px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                Great Resume
              </span>
            </div>

            <div className="flex-1">
              <h4 className="text-sm font-semibold text-gray-900 mb-3">
                Core Assessment
              </h4>

              <div className="flex gap-2 mb-4 border-b border-gray-200">
                <button
                  onClick={() => setActiveResumeTab("recommendation")}
                  className={`px-4 py-2 text-sm font-medium transition-colors rounded-full ${
                    activeResumeTab === "recommendation"
                      ? "text-white bg-black rounded-full"
                      : "text-blue-900 hover:text-blue-800"
                  }`}
                >
                  <div className="flex items-center gap-1">
                    <CheckCircle className="w-4 h-4" />
                    Recommendation
                  </div>
                </button>
                <button
                  onClick={() => setActiveResumeTab("strength")}
                  className={`px-4 py-2 text-sm font-medium transition-colors rounded-full ${
                    activeResumeTab === "strength"
                      ? "text-white bg-black rounded-full"
                      : "text-green-800 hover:text-green-700"
                  }`}
                >
                  <div className="flex items-center gap-1">
                    <CheckCircle className="w-4 h-4" />
                    Strength
                  </div>
                </button>
                <button
                  onClick={() => setActiveResumeTab("flags")}
                  className={`px-4 py-2 text-sm font-medium transition-colors rounded-full ${
                    activeResumeTab === "flags"
                      ? "text-white bg-black rounded-full"
                      : "text-red-500 hover:text-red-700"
                  }`}
                >
                  <div className="flex items-center gap-1">
                    <XCircle className="w-4 h-4" />
                    Flags
                  </div>
                </button>
              </div>

              <div className="min-h-[100px]">
                {activeResumeTab === "recommendation" && (
                  <p className="text-sm text-gray-700">
                    Strong professional background with consistent career
                    progression in relevant fields. Educational qualifications
                    align well with job requirements.
                  </p>
                )}
                {activeResumeTab === "strength" && (
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <span className="text-gray-400">•</span>
                      <span>
                        Extensive experience in senior roles at reputable
                        companies
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-gray-400">•</span>
                      <span>
                        Strong technical and leadership skills demonstrated
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-gray-400">•</span>
                      <span>
                        Relevant certifications and continuous learning
                      </span>
                    </li>
                  </ul>
                )}
                {activeResumeTab === "flags" && (
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <span className="text-gray-400">•</span>
                      <span>6-month gap between 2020 and 2021</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-gray-400">•</span>
                      <span>Frequent job changes since 2019</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-gray-400">•</span>
                      <span>Overlapping employment dates at two companies</span>
                    </li>
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div> */}
      </div>
    </div>
  );
};

export default InterviewReports;
